import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

type Summary = {
  studioName: string | null;
  startAt: string | null;
  endAt: string | null;
  total: number;
  addons: { label: string; qty: number; total: string }[];
};

const BookingSuccess = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState<"verifying" | "paid" | "unpaid" | "error">("verifying");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [pollCount, setPollCount] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); setErrorMsg("Missing checkout session ID in the URL."); return; }
    cancelled.current = false;
    // Poll the verify endpoint — Stripe redirect can land here before the
    // webhook has marked the booking paid, so retry for up to ~40s.
    const MAX_ATTEMPTS = 20;
    const DELAY_MS = 2000;
    let attempt = 0;
    const tick = async () => {
      if (cancelled.current) return;
      attempt += 1;
      setPollCount(attempt);
      const { data, error } = await supabase.functions.invoke("stripe-verify-booking", { body: { session_id: sessionId } });
      if (cancelled.current) return;
      if (error || data?.error) {
        if (attempt >= MAX_ATTEMPTS) {
          setState("error");
          setErrorMsg((error as Error)?.message || data?.error || "We couldn't verify your payment. Please contact support.");
          return;
        }
        setTimeout(tick, DELAY_MS);
        return;
      }
      if (data?.summary) setSummary(data.summary as Summary);
      if (data?.paid) { setState("paid"); return; }
      // Not paid yet — keep polling; webhook may still be propagating.
      if (attempt >= MAX_ATTEMPTS) { setState("unpaid"); return; }
      setTimeout(tick, DELAY_MS);
    };
    tick();
    return () => { cancelled.current = true; };
  }, [params]);

  const fmtDate = (s: string | null) =>
    s ? new Date(s).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div className="pt-32 pb-20 min-h-[60vh] flex items-center justify-center">
      <div className="max-w-lg mx-auto text-center px-4 w-full">
        {state === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="font-display tracking-widest">VERIFYING PAYMENT…</p>
            {pollCount > 2 && (
              <p className="text-xs text-muted-foreground font-mono tracking-widest mt-3">
                Still confirming with Stripe… (attempt {pollCount})
              </p>
            )}
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-display tracking-widest mb-3">BOOKING CONFIRMED</h1>
            <p className="text-muted-foreground font-body mb-8">You'll receive a confirmation email shortly with shoot details and studio rules.</p>
            {summary && (
              <div className="bg-panel border border-border p-6 text-left space-y-2 mb-8">
                {summary.studioName && (
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground font-mono uppercase text-xs tracking-widest">Studio</span><span>{summary.studioName}</span></div>
                )}
                {summary.startAt && (
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground font-mono uppercase text-xs tracking-widest">When</span><span>{fmtDate(summary.startAt)} → {fmtDate(summary.endAt)}</span></div>
                )}
                {summary.addons.length > 0 && (
                  <div className="border-t border-border pt-2 mt-2">
                    <p className="text-muted-foreground font-mono uppercase text-[11px] tracking-widest mb-2">Add-ons</p>
                    {summary.addons.map((a, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{a.label}{a.qty > 1 ? ` × ${a.qty}` : ""}</span>
                        <span className="font-mono">${a.total}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t border-border pt-2 mt-2 flex justify-between">
                  <span className="font-display tracking-widest">TOTAL PAID</span>
                  <span className="font-display text-primary">${summary.total.toFixed(2)}</span>
                </div>
              </div>
            )}
            <Link to="/account"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest">VIEW MY BOOKINGS</Button></Link>
          </>
        )}
        {(state === "unpaid" || state === "error") && (
          <>
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h1 className="text-3xl font-display tracking-widest mb-3">
              {state === "error" ? "VERIFICATION FAILED" : "PAYMENT NOT COMPLETED"}
            </h1>
            <p className="text-muted-foreground font-body mb-8">
              {errorMsg || "Your booking is still pending. If you completed payment, it can take a moment to confirm — refresh in a few seconds. Otherwise, try again from the booking page."}
            </p>
            <Link to="/book"><Button variant="outline" className="font-display tracking-widest">BACK TO BOOKING</Button></Link>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingSuccess;
